// @ts-strict-ignore
import {inject, Injectable, signal} from "@angular/core";
import {FormArray, FormBuilder, FormControl, FormGroup} from "@angular/forms";
import {Checklist, ChecklistItem} from "@models";
import {debounceTime, filter, startWith, tap} from "rxjs/operators";
import {AssessableAnswerService} from "./assessment-answer.service";
import {combineLatest, defer, merge} from "rxjs";

type UpdateAnswerCallbackParams = {
	answer: any;
	checklistId: string;
	checklistItemId: string;
};
type UpdateAnswerCallback = (data: UpdateAnswerCallbackParams) => void;
type ItemFormGroup = FormGroup<{
	// required for tracking the item
	id: FormControl<string>;
	answer: FormControl;
}>;

@Injectable()
export class ChecklistAnswerService extends AssessableAnswerService {
	private _fb = inject(FormBuilder);
	private _updateAnswerCallback: UpdateAnswerCallback;
	private _answerObservables: Array<ItemFormGroup["valueChanges"]> = [];
	private _checklist: Checklist;
	private _isInitialized: boolean;
	private _answeredItemsMap = signal(new Map<string, Map<string, boolean>>());

	public answeredItemsMap = this._answeredItemsMap.asReadonly();
	public $savingPipeline = combineLatest([
		defer(() => this.form.valueChanges.pipe(startWith(this.form.value))).pipe(tap((value) => this._fillAnsweredItemsMap(value))),
		defer(() => merge(...this._answerObservables)).pipe(
			filter((_) => this.form.valid),
			filter((value) => value.answer !== undefined && value.answer !== null),
			debounceTime(500),
			tap((value) =>
				this._updateAnswerCallback({
					answer: value.answer,
					checklistId: this._checklist.id,
					checklistItemId: value.id,
				}),
			),
		),
	]);

	public form: FormGroup<{
		assessableGroups: FormArray<
			FormGroup<{
				// required for tracking the group
				id: FormControl<string>;
				items: FormArray<ItemFormGroup>;
			}>
		>;
		ungroupedItems: FormArray<ItemFormGroup>;
	}>;

	public initialize(checklist: Checklist, updateAnswerCallback: (data: UpdateAnswerCallbackParams) => void) {
		this._checklist = checklist;
		if (this._isInitialized) {
			return;
		}
		this._updateAnswerCallback = updateAnswerCallback;

		const createItemControlAndSubscribe = (item: ChecklistItem) => {
			const itemControl = this._createItemControl(item);
			this._answerObservables.push(itemControl.valueChanges);
			return itemControl;
		};

		this.form = this._fb.group({
			assessableGroups: this._fb.array(
				checklist.assessableGroups.map((group) =>
					this._fb.group({
						id: group.groupId,
						items: this._fb.array(group.items.map(createItemControlAndSubscribe)),
					}),
				),
			),
			ungroupedItems: this._fb.array(checklist.ungroupedItems.map(createItemControlAndSubscribe)),
		});
		this._isInitialized = true;
	}

	public findItemControl(itemId: string) {
		const groupedItemControls = this.form.controls.assessableGroups.controls.flatMap((g) => g.controls.items.controls);
		const ungroupedItemControls = this.form.controls.ungroupedItems.controls;
		const itemControls = [...groupedItemControls, ...ungroupedItemControls];
		return itemControls.find((c) => c.value.id === itemId);
	}

	public isGroupActivated(_groupId: string): boolean {
		// Checklist groups are always active
		return true;
	}

	private _createItemControl(item: ChecklistItem) {
		return this._fb.group({
			id: this._fb.control(item.assessableItemId),
			answer: this._fb.control(item.answer),
		});
	}

	private _fillAnsweredItemsMap(value: typeof this.form.value) {
		const answeredItemsMap = new Map<string, Map<string, boolean>>();
		// fill map for grouped items
		for (const group of value.assessableGroups) {
			answeredItemsMap.set(group.id, new Map<string, boolean>());
			for (const {id, answer} of group.items) {
				const isAnswered = (Array.isArray(answer) && answer.length > 0) || (answer !== undefined && answer !== null);
				answeredItemsMap.get(group.id).set(id, isAnswered);
			}
		}

		// fill map for ungrouped items
		for (const {id, answer} of value.ungroupedItems) {
			if (!answeredItemsMap.has(null)) {
				answeredItemsMap.set(null, new Map<string, boolean>());
			}
			const isAnswered = (Array.isArray(answer) && answer.length > 0) || (answer !== undefined && answer !== null);
			answeredItemsMap.get(null).set(id, isAnswered);
		}
		this._answeredItemsMap.set(answeredItemsMap);
	}
}
